/**
 * EncounterAuditDetail Component
 *
 * Detail page showing encounter metadata, analysis, and all note audits
 */

import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PatientContextBar } from '@features/patient-context-bar';
import {
  ArrowBack as BackIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CompliantIcon,
  CompareArrows as CompareIcon,
  LocalHospital as ProviderIcon,
  OpenInNew as OpenIcon,
  Person as PersonIcon,
  Schedule as DurationIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { Patient } from '@ts/types';

import { useEncounterAuditDetail } from '../../hooks/useEncounterAuditDetail';
import { isValidNoteId } from '../../utils/validation';
import { JotAuditPageLayout } from '../layout';
import { DeltaBadge } from '../shared/DeltaBadge';
import { QualityScore } from '../shared/QualityScore';

import { EncounterAnalysisSidebar } from './EncounterAnalysisSidebar';

export const EncounterAuditDetail: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { encounterId, organizationId } = useParams<{
    encounterId: string;
    organizationId?: string;
  }>();

  const { encounter, isLoading, error, refetch } = useEncounterAuditDetail({
    encounterId,
    organizationId,
  });

  const handleBack = () => {
    if (organizationId) {
      navigate(`/admin-v2/jotaudit/${organizationId}?tab=encounters`);
    } else {
      navigate('/jotaudit?tab=encounters');
    }
  };

  const handleNoteClick = (noteId: string) => {
    if (!isValidNoteId(noteId)) return;

    if (organizationId) {
      navigate(`/admin-v2/jotaudit/${organizationId}/note/${noteId}`);
    } else {
      navigate(`/jotaudit/note/${noteId}`);
    }
  };

  const handlePatientClick = (patientId: string) => {
    navigate(`/patient/${patientId}`);
  };

  // Convert encounter patient data to Patient type for PatientContextBar
  const patientForContextBar: Patient | null = encounter?.patient
    ? ({
        patient_id: encounter.patient.patient_id,
        first_name: encounter.patient.first_name || '',
        last_name: encounter.patient.last_name || '',
        preferred_name: encounter.patient.preferred_name,
        dob: encounter.patient.dob,
        gender: encounter.patient.gender,
        pronouns: encounter.patient.pronouns,
        email: encounter.patient.email,
        mobile_phone: encounter.patient.mobile_phone,
      } as Patient)
    : null;

  /**
   * Format date to display format
   */
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    } catch {
      return '—';
    }
  };


  if (error) {
    return (
      <JotAuditPageLayout>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={refetch}>
              Retry
            </Button>
          }
        >
          <AlertTitle>Error loading encounter</AlertTitle>
          {error}
        </Alert>
      </JotAuditPageLayout>
    );
  }

  if (isLoading || !encounter) {
    return (
      <JotAuditPageLayout>
        <Skeleton variant="rectangular" sx={{height: 200,  mb: 3, borderRadius: 2 }} />
        <Skeleton variant="rectangular" sx={{height: 400,  mb: 3, borderRadius: 2 }} />
        <Skeleton variant="rectangular" sx={{height: 300,  borderRadius: 2 }} />
      </JotAuditPageLayout>
    );
  }

  return (
    <JotAuditPageLayout>
      {/* Back Button */}
      <Button startIcon={<BackIcon />} onClick={handleBack} sx={{ mb: 2 }} size="small">
        Back to Encounters
      </Button>

      {/* Encounter Header Card - Condensed */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 2,
          p: 2,
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center', mb: 1.5 }}>
          {/* Date/Time */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CalendarIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {formatDate(encounter.appointment?.start_time ?? encounter.scheduled_for)}
            </Typography>
          </Box>

          {/* Patient */}
          {encounter.patient_name && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PersonIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
              <Typography variant="body2">{encounter.patient_name}</Typography>
            </Box>
          )}

          {/* Provider */}
          {encounter.provider_name && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ProviderIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
              <Typography variant="body2">{encounter.provider_name}</Typography>
            </Box>
          )}

          {/* Status & Modality */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Chip label={encounter.status} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
            {encounter.modality && (
              <Chip label={encounter.modality} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
            )}
          </Box>
        </Box>

        {/* Codes Row */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {encounter.cpt_codes && encounter.cpt_codes.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>
                CPT:
              </Typography>
              {encounter.cpt_codes.map((code, idx) => (
                <Chip key={idx} label={code} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
              ))}
            </Box>
          )}

          {encounter.icd10_codes && encounter.icd10_codes.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>
                ICD-10:
              </Typography>
              {encounter.icd10_codes.map((code, idx) => (
                <Chip key={idx} label={code} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
              ))}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Patient Context Bar */}
      {patientForContextBar && (
        <Box sx={{ mb: 2 }}>
          <PatientContextBar
            patient={patientForContextBar}
            onPatientClick={handlePatientClick}
            editable={false}
            source="jotaudit_encounter"
          />
        </Box>
      )}

      {/* Appointment Details - Condensed Inline */}
      {encounter.appointment && (
        <Paper
          elevation={0}
          sx={{
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 2,
            p: 1.5,
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', fontSize: '0.875rem' }}>
            {encounter.appointment.duration && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <DurationIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                  {encounter.appointment.duration} min
                </Typography>
              </Box>
            )}
            {encounter.appointment.telehealth !== undefined && (
              <Chip
                label={encounter.appointment.telehealth ? 'Telehealth' : 'In-Person'}
                size="small"
                sx={{ height: 18, fontSize: '0.65rem' }}
              />
            )}
            {encounter.appointment.encounter_type && (
              <Chip
                label={encounter.appointment.encounter_type}
                size="small"
                variant="outlined"
                sx={{ height: 18, fontSize: '0.65rem' }}
              />
            )}
          </Box>
        </Paper>
      )}

      {/* Main Note Audit and Encounter Analysis - Side by Side */}
      {encounter.note_audits && encounter.note_audits.length > 0 && (() => {
        const mainAudit = encounter.note_audits[0];
        const additionalAudits = encounter.note_audits.slice(1);
        const revision = mainAudit.current_revision;
        const auditData = revision?.audit_data;
        
        // Calculate rule compliance summary
        const totalRules = auditData?.rule_compliance_assessments?.length || 0;
        const passedRules = auditData?.rule_compliance_assessments?.filter(
          (a: any) => a.compliance_status === 'yes'
        ).length || 0;
        
        return (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {/* Main Note Audit - Left Column */}
              <Grid size={{ xs: 12, lg: 6 }}>
            <Paper
              elevation={0}
              sx={{
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: 2,
                overflow: 'hidden',
                mb: 2,
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  bgcolor: theme.palette.grey[50],
                  px: 2,
                  py: 1.5,
                  borderBottom: `1px solid ${theme.palette.grey[200]}`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {mainAudit.audit.note_title || 'Primary Note Audit'}
                </Typography>
                <Button
                  size="small"
                  endIcon={<OpenIcon />}
                  onClick={() => handleNoteClick(mainAudit.audit.note_id)}
                  sx={{ textTransform: 'none' }}
                >
                  View Full Audit
                </Button>
              </Box>

              {/* CPT Mismatch Warning */}
              {revision?.cpt_match === false && (
                <Alert severity="warning" icon={<WarningIcon />} sx={{ m: 2, mb: 0 }}>
                  <AlertTitle>CPT Code Mismatch</AlertTitle>
                  Provider code
                  {revision.provider_cpt_codes && revision.provider_cpt_codes.length > 1 ? 's' : ''} (
                  {revision.provider_cpt_codes && revision.provider_cpt_codes.length > 0
                    ? revision.provider_cpt_codes.join(', ')
                    : '—'}
                  ){' '}
                  {revision.provider_cpt_codes && revision.provider_cpt_codes.length > 1
                    ? 'differ'
                    : 'differs'}{' '}
                  from AI recommendation
                  {(revision?.recommended_cpt_codes?.length ?? 0) > 1 ? 's' : ''} (
                  {revision?.recommended_cpt_codes && revision.recommended_cpt_codes.length > 0
                    ? revision.recommended_cpt_codes.join(', ')
                    : '—'}
                  ). Review documentation to ensure proper billing.
                </Alert>
              )}

              <Box sx={{ p: 2 }}>
                {/* Summary Cards */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {/* Quality Score */}
                  <Grid size={{ xs: 6, md: 3 }}>
                    <Box
                      sx={{
                        p: 1.5,
                        border: `1px solid ${theme.palette.grey[200]}`,
                        borderRadius: 2,
                        bgcolor: theme.palette.grey[50],
                        minHeight: 90,
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mb: 0.5, fontSize: '0.7rem' }}>
                        Quality Score
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 'auto' }}>
                        <QualityScore
                          score={revision?.overall_quality_score}
                          variant="badge"
                          size="medium"
                        />
                        {revision?.quality_delta !== null && revision?.quality_delta !== undefined && (
                          <DeltaBadge delta={revision.quality_delta} size="small" />
                        )}
                      </Box>
                    </Box>
                  </Grid>

                  {/* Compliance */}
                  <Grid size={{ xs: 6, md: 3 }}>
                    <Box
                      sx={{
                        p: 1.5,
                        border: `1px solid ${theme.palette.grey[200]}`,
                        borderRadius: 2,
                        bgcolor: theme.palette.grey[50],
                        minHeight: 90,
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mb: 0.5, fontSize: '0.7rem' }}>
                        Compliance
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 'auto' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.125rem', whiteSpace: 'nowrap' }}>
                          {revision?.compliance_percentage !== null &&
                          revision?.compliance_percentage !== undefined
                            ? `${revision.compliance_percentage.toFixed(0)}%`
                            : '—'}
                        </Typography>
                        {revision?.compliance_delta !== null &&
                          revision?.compliance_delta !== undefined && (
                            <DeltaBadge delta={revision.compliance_delta} label="%" size="small" />
                          )}
                      </Box>
                    </Box>
                  </Grid>

                  {/* Rules Applied */}
                  <Grid size={{ xs: 6, md: 3 }}>
                    <Box
                      sx={{
                        p: 1.5,
                        border: `1px solid ${theme.palette.grey[200]}`,
                        borderRadius: 2,
                        bgcolor: theme.palette.grey[50],
                        minHeight: 90,
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mb: 0.5, fontSize: '0.7rem' }}>
                        Rules Applied
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.125rem', mt: 'auto' }}>
                        {totalRules}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Revisions */}
                  <Grid size={{ xs: 6, md: 3 }}>
                    <Box
                      sx={{
                        p: 1.5,
                        border: `1px solid ${theme.palette.grey[200]}`,
                        borderRadius: 2,
                        bgcolor: theme.palette.grey[50],
                        minHeight: 90,
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mb: 0.5, fontSize: '0.7rem' }}>
                        Revisions
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.125rem', mt: 'auto' }}>
                        {mainAudit.audit.total_revisions || 1}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* CPT Codes Display */}
                {(() => {
                  const targetCodes = revision?.target_cpt_codes;
                  const cptSource = revision?.target_cpt_source;
                  const isMatch = revision?.cpt_match;
                  const recommendedCodes = revision?.recommended_cpt_codes;
                  const billingSupport = auditData?.billing_support;

                  // Check if we have codes to display
                  if (!targetCodes || targetCodes.length === 0) {
                    return null;
                  }

                  // Check if recommended codes differ from target codes
                  const targetCodesSet = new Set(targetCodes.filter(c => c && c.trim()));
                  const recommendedCodesSet = new Set((recommendedCodes ?? []).filter(c => c && c.trim()));
                  const hasDifferentSuggestion = Boolean(
                    recommendedCodes && 
                    recommendedCodes.length > 0 &&
                    (targetCodesSet.size !== recommendedCodesSet.size ||
                     [...targetCodesSet].some(c => !recommendedCodesSet.has(c)))
                  );

                  // Determine scenario styling
                  let cardColor: string;
                  let statusLabel: string;
                  let StatusIcon: typeof CompliantIcon;

                  if (cptSource === 'user') {
                    if (isMatch) {
                      // User codes, AI agrees
                      cardColor = theme.palette.success.main;
                      statusLabel = 'Codes Supported';
                      StatusIcon = CompliantIcon;
                    } else {
                      // User codes, AI suggests different
                      cardColor = theme.palette.warning.main;
                      statusLabel = 'Review Suggested';
                      StatusIcon = WarningIcon;
                    }
                  } else {
                    // AI suggested codes for audit
                    cardColor = theme.palette.info.main;
                    statusLabel = 'Suggested Codes';
                    StatusIcon = CompareIcon;
                  }

                  return (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        CPT Codes
                      </Typography>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 1.5,
                          bgcolor: `${cardColor}08`,
                          border: `1px solid ${cardColor}25`,
                        }}
                      >
                        {/* Header row: Status badge */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            color: cardColor,
                            mb: 1.25,
                          }}
                        >
                          <StatusIcon sx={{ fontSize: 16 }} />
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 700, letterSpacing: '0.02em' }}
                          >
                            {statusLabel}
                          </Typography>
                        </Box>

                        {/* Primary Section: What the audit was based on */}
                        <Box sx={{ mb: hasDifferentSuggestion ? 1.5 : 0 }}>
                          {/* Label for audit basis */}
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              color: 'textColors.secondaryText',
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                              mb: 0.5,
                            }}
                          >
                            {cptSource === 'user' ? 'Audit based on provider codes' : 'Based on documentation'}
                          </Typography>

                          {/* Target CPT Codes */}
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 0.75,
                              alignItems: 'center',
                            }}
                          >
                            {targetCodes.filter(c => c && c.trim()).map((code, idx) => (
                              <Chip
                                key={`target-${idx}`}
                                label={code}
                                size="small"
                                sx={{
                                  fontFamily: 'monospace',
                                  fontWeight: 700,
                                  fontSize: '0.8rem',
                                  bgcolor: `${cardColor}20`,
                                  color: cardColor,
                                  border: `2px solid ${cardColor}`,
                                  height: '28px',
                                  '& .MuiChip-label': {
                                    px: 1.5,
                                  },
                                }}
                              />
                            ))}
                          </Box>
                        </Box>

                        {/* Secondary Section: AI Suggestion (only if different from target) */}
                        {hasDifferentSuggestion && (
                          <Box
                            sx={{
                              pt: 1.25,
                              borderTop: `1px dashed ${theme.palette.grey[300]}`,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                color: 'textColors.secondaryText',
                                fontSize: '0.65rem',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                mb: 0.5,
                              }}
                            >
                              AI recommends instead
                            </Typography>

                            {/* Recommended codes */}
                            <Box
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 0.75,
                                alignItems: 'center',
                              }}
                            >
                              {recommendedCodes?.filter(c => c && c.trim()).map((code, idx) => (
                                <Chip
                                  key={`recommended-${idx}`}
                                  label={code}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    fontFamily: 'monospace',
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    borderColor: theme.palette.primary.main,
                                    color: theme.palette.primary.main,
                                    height: '24px',
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                        )}

                        {/* Concern - compact, only if exists */}
                        {billingSupport?.concerns?.[0] && (
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              mt: 1,
                              color: 'warning.dark',
                              lineHeight: 1.3,
                              fontSize: '0.7rem',
                            }}
                          >
                            ⚠ {billingSupport.concerns[0]}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })()}

                {/* Enrichment Rationale - Admin only */}
                {organizationId && (() => {
                  const enrichment = revision?.audit_data?.enrichment_rationale;
                  if (!enrichment) return null;

                  return (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Enrichment Rationale
                      </Typography>

                      {/* Summary */}
                      {enrichment.summary && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.secondary,
                            lineHeight: 1.5,
                            mb: 1.5,
                            fontSize: '0.8rem',
                          }}
                        >
                          {enrichment.summary}
                        </Typography>
                      )}

                      {/* Per-code rationale */}
                      {enrichment.line_item_rationale?.map((item: any, idx: number) => (
                        <Box
                          key={idx}
                          sx={{
                            p: 1.5,
                            mb: 1,
                            borderRadius: 1.5,
                            bgcolor: theme.palette.grey[50],
                            border: `1px solid ${theme.palette.grey[200]}`,
                          }}
                        >
                          {/* Code header + confidence */}
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 1,
                            }}
                          >
                            <Chip
                              label={item.code_id}
                              size="small"
                              sx={{
                                fontFamily: 'monospace',
                                fontWeight: 700,
                                fontSize: '0.75rem',
                                bgcolor: `${theme.palette.primary.main}15`,
                                color: theme.palette.primary.main,
                                border: `1px solid ${theme.palette.primary.main}40`,
                                height: 24,
                                '& .MuiChip-label': { px: 1 },
                              }}
                            />
                            {item.confidence != null && (
                              <Chip
                                label={`${Math.round(item.confidence * 100)}% confidence`}
                                size="small"
                                sx={{
                                  fontSize: '0.65rem',
                                  fontWeight: 600,
                                  height: 20,
                                  bgcolor:
                                    item.confidence >= 0.8
                                      ? `${theme.palette.success.main}12`
                                      : item.confidence >= 0.5
                                        ? `${theme.palette.warning.main}12`
                                        : `${theme.palette.error.main}12`,
                                  color:
                                    item.confidence >= 0.8
                                      ? theme.palette.success.dark
                                      : item.confidence >= 0.5
                                        ? theme.palette.warning.dark
                                        : theme.palette.error.dark,
                                  '& .MuiChip-label': { px: 0.75 },
                                }}
                              />
                            )}
                          </Box>

                          {/* Rationale text */}
                          {item.rationale && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: theme.palette.text.secondary,
                                fontSize: '0.78rem',
                                lineHeight: 1.5,
                                mb: item.evidence?.length > 0 ? 1 : 0,
                              }}
                            >
                              {item.rationale}
                            </Typography>
                          )}

                          {/* Evidence list */}
                          {item.evidence?.length > 0 && (
                            <Box sx={{ mt: 0.5 }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 600,
                                  color: theme.palette.text.secondary,
                                  fontSize: '0.65rem',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.05em',
                                  display: 'block',
                                  mb: 0.5,
                                }}
                              >
                                Supporting Evidence
                              </Typography>
                              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                {item.evidence.map((e: string, eIdx: number) => (
                                  <Box
                                    component="li"
                                    key={eIdx}
                                    sx={{
                                      mb: 0.25,
                                      '&::marker': { color: theme.palette.grey[400] },
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: theme.palette.text.secondary,
                                        lineHeight: 1.4,
                                        fontSize: '0.7rem',
                                      }}
                                    >
                                      {e}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  );
                })()}
              </Box>
            </Paper>
              </Grid>

              {/* Encounter Analysis - Right Column */}
              <Grid size={{ xs: 12, lg: 6 }}>
                {encounter.encounter_analysis && (
                  <EncounterAnalysisSidebar encounterAnalysis={encounter.encounter_analysis} />
                )}
              </Grid>
            </Grid>

            {/* Additional Note Audits */}
            {additionalAudits.length > 0 && (
              <Paper
                elevation={0}
                sx={{
                  border: `1px solid ${theme.palette.grey[200]}`,
                  borderRadius: 2,
                  overflow: 'hidden',
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    bgcolor: theme.palette.grey[50],
                    px: 2,
                    py: 1,
                    borderBottom: `1px solid ${theme.palette.grey[200]}`,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Additional Note Audits ({additionalAudits.length})
                  </Typography>
                </Box>

                <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {additionalAudits.map((noteAudit, idx) => (
                    <Box
                      key={idx}
                      onClick={() => handleNoteClick(noteAudit.audit.note_id)}
                      sx={{
                        border: `1px solid ${theme.palette.grey[200]}`,
                        borderRadius: 1,
                        p: 1.5,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: theme.palette.grey[50],
                          borderColor: theme.palette.primary.main,
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                            {noteAudit.audit.note_title || 'Untitled Note'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.7rem' }}>
                            {noteAudit.current_revision && (
                              <>Rev {noteAudit.current_revision.revision} • {noteAudit.current_revision.status}</>
                            )}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexShrink: 0 }}>
                          {/* Compliance */}
                          {noteAudit.audit.latest_compliance_pct !== null &&
                            noteAudit.audit.latest_compliance_pct !== undefined && (
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography
                                  variant="caption"
                                  sx={{ color: theme.palette.text.disabled, display: 'block', fontSize: '0.65rem', mb: 0.25 }}
                                >
                                  Compliance
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: '0.8rem',
                                    color:
                                      noteAudit.audit.latest_compliance_pct >= 80
                                        ? theme.palette.success.main
                                        : noteAudit.audit.latest_compliance_pct >= 60
                                          ? theme.palette.warning.main
                                          : theme.palette.error.main,
                                  }}
                                >
                                  {noteAudit.audit.latest_compliance_pct.toFixed(0)}%
                                </Typography>
                              </Box>
                            )}

                          {/* Quality Score */}
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography
                              variant="caption"
                              sx={{ color: theme.palette.text.disabled, display: 'block', fontSize: '0.65rem', mb: 0.25 }}
                            >
                              Quality
                            </Typography>
                            <QualityScore
                              score={noteAudit.audit.latest_quality_score}
                              variant="badge"
                              size="small"
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Paper>
            )}
          </>
        );
      })()}

      {/* No Audits Message */}
      {(!encounter.note_audits || encounter.note_audits.length === 0) && (
        <>
        <Paper
          elevation={0}
          sx={{
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 2,
            p: 2,
            mb: 2,
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" sx={{ color: theme.palette.text.disabled, fontSize: '0.8rem' }}>
            No note audits found for this encounter
          </Typography>
        </Paper>

        {/* Encounter Analysis when no audits */}
        {encounter.encounter_analysis && (
          <Box sx={{ mb: 2 }}>
            <EncounterAnalysisSidebar encounterAnalysis={encounter.encounter_analysis} />
          </Box>
        )}
      </>
      )}

    </JotAuditPageLayout>
  );
};
